<template>
  <AssetCard
    :view-mode="viewMode"
    :empty-title="agent ? null : 'Aucun agent associé'"
    :empty-message="agent ? null : 'Ajoutez un agent pour documenter les intervenants'"
    :created-at="agent?.created_at ?? null"
    :memos-count="computedMemosCount"
    :show-edit-button="showEditButton && !!agent"
    :show-delete-button="showDeleteButton && !!agent"
    :table-grid-class="'flex items-center'"
    :is-owned="isOwned"
    :is-public="isPublic"
    :is-private="!isPublic"
    :show-ownership-badge="showOwnershipBadge"
    :user-id="userId"
    :can-edit="canEditAgent"
    :can-delete="canDeleteAgent"
    @click="handleClick"
    @edit="agent && $emit('edit', agent)"
    @delete="handleDelete"
  >
    <!-- Mode Table -->
    <template #table-row="{ classes }">
      <!-- Nom et IdRef -->
      <div class="flex items-center gap-2 min-w-0 flex-[1.25]">
        <FileUserIcon :class="classes.icon" />
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <span :class="[classes.title, 'truncate']">
              <span v-if="!agent?.is_legal_entity">
                <span>{{ getDisplayNameParts(agent).firstName }} {{ getDisplayNameParts(agent).lastName }}</span>
              </span>
              <span v-else>
                {{ getDisplayNameParts(agent).affiliation }}
              </span>
            </span>
            <a
              v-if="agent?.idref_id" 
              :href="agent?.is_legal_entity ? getStructureLink(agent.idref_id) : getAureHalLink(agent.idref_id)"
              target="_blank"
              :class="classes.link"
            >
              <LinkIcon :class="classes.linkIcon" />
            </a>
          </div>
        </div>
      </div>

      <!-- Affiliation (masquée pour les personnes morales car déjà dans le titre) -->
      <div class="hidden md:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">
          {{ !agent?.is_legal_entity ? (agent?.affiliation || 'Non spécifié') : 'Structure' }}
        </span>
      </div>

      <!-- Groupe -->
      <div class="hidden lg:flex items-center text-sm text-gray-600 min-w-0 flex-1 px-3">
        <span class="truncate">{{ agent?.group || 'Non spécifié' }}</span>
      </div>

      <!-- Nombre de mémos -->
      <div class="flex items-center gap-1 text-sm text-gray-600 min-w-0 px-3">
        <ClipboardIcon :class="classes.footerIcon" />
        <span>{{ computedMemosCount }}</span>
      </div>

      <!-- Actions -->
      <TableRowActions
        :classes="classes"
        :show-edit-button="showEditButton"
        :show-delete-button="showDeleteButton"
        :can-edit="canEditAgent"
        :can-delete="canDeleteAgent"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </template>

    <!-- Mode Memos Table -->
    <template #memos-table-icon>
      <FileUserIcon class="h-5 w-5 text-gray-500 shrink-0" />
    </template>

    <template #memos-table-content="{ classes }">
      <template v-if="agent">
        <div class="flex items-center gap-2">
          <h3 :class="classes.title">
            <span v-if="!agent?.is_legal_entity">
              <span>{{ getDisplayNameParts(agent).firstName }} {{ getDisplayNameParts(agent).lastName }}</span>
            </span>
            <span v-else>
              {{ getDisplayNameParts(agent).affiliation }}
            </span>
          </h3>
          <a
v-if="agent.idref_id" 
             :href="getAureHalLink(agent.idref_id)" 
             target="_blank"
             :class="classes.link">
            <LinkIcon :class="classes.linkIcon" />
          </a>
        </div>
        <div class="flex items-center gap-2 text-xs text-gray-500">
          <span v-if="agent.affiliation" class="truncate">{{ agent.affiliation }}</span>
          <span v-if="agent.affiliation && agent.group" class="text-gray-300 shrink-0">•</span>
          <span v-if="agent.group" class="truncate">{{ agent.group }}</span>
        </div>
        <div v-if="getRoles(roles).length > 0" class="flex flex-wrap gap-1 mt-1">
          <span
v-for="role in getRoles(roles)" 
                :key="role" 
                class="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] leading-none whitespace-nowrap">
            {{ role }}
          </span>
        </div>
      </template>
      <div v-else class="text-gray-500 text-sm flex items-center gap-2">
        <span>Aucun agent associé</span>
      </div>
    </template>

    <!-- Mode Default -->
    <template #default="{ classes }">
      <!-- Icône de type d'agent en haut à gauche -->
      <div class="absolute top-2 left-2 z-10">
        <div 
          :class="[
            'w-6 h-6 rounded-full flex items-center justify-center text-white shadow-sm',
            agent?.is_legal_entity ? 'bg-purple-500' : 'bg-blue-500'
          ]"
          :title="agent?.is_legal_entity ? 'Personne morale' : 'Personne physique'"
        >
          <BuildingIcon v-if="agent?.is_legal_entity" class="w-3.5 h-3.5" />
          <UserIcon v-else class="w-3.5 h-3.5" />
        </div>
      </div>

      <div class="flex items-center gap-4 mb-2 mt-3">
        <h2 :class="classes.title">
          <span v-if="!agent?.is_legal_entity">
            <span>{{ getDisplayNameParts(agent).firstName }} {{ getDisplayNameParts(agent).lastName }}</span>
          </span>
          <span v-else>
            {{ getDisplayNameParts(agent).affiliation }}
          </span>
        </h2>
        <div v-if="agent?.idref_id" class="flex items-center">
          <a
            :href="agent?.is_legal_entity ? getStructureLink(agent.idref_id) : getAureHalLink(agent.idref_id)" 
            target="_blank"
            :class="classes.link"
          >
            <LinkIcon :class="classes.linkIcon" />
          </a>
        </div>
      </div>
    </template>

    <!-- Mode Default Info -->
    <template #info="{ classes }">
      <!-- Affiliation seulement pour les personnes physiques -->
      <div v-if="agent?.affiliation && !agent?.is_legal_entity" :class="classes.iconWithText">
        <Building2Icon :class="classes.icon" />
        <span>{{ agent.affiliation }}</span>
        <a
          v-if="agent.affiliation_id"
          :href="getStructureLink(agent.affiliation_id)" 
          target="_blank"
          :class="classes.link"
        >
          <LinkIcon :class="classes.linkIcon" />
        </a>
      </div>

      <div v-if="agent?.group" :class="classes.iconWithText">
        <UsersIcon :class="classes.icon" />
        <span>{{ agent.group }}</span>
      </div>
      <!-- Tags/Rôles en bulles - juste au-dessus du footer -->
<div v-if="agent?.roles && getRoles(agent.roles).length > 0" class="absolute bottom-0 left-0 right-0">
  <div class="flex flex-wrap gap-1.5 p-4">
    <span
      v-for="role in getRoles(agent.roles)"
      :key="role"
      class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors duration-200"
    >
      {{ role }}
    </span>
  </div>
</div>
    </template>

    
    <template #footer-left="{ classes }">
      <div class="flex flex-col gap-2">
 
        
        <!-- Date en dessous -->
        <span
          v-if="agent?.created_at"
          class="flex items-center gap-2"
          :class="classes.footerText"
        >
          <ClockIcon :class="classes.footerIcon" />
          {{ formatDate(agent.created_at) }}
        </span>
      </div>
    </template>
  </AssetCard>


</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import type { PropType } from 'vue';
import type { AgentWithMemos } from '../../api/types/entities/agent/relations';
import {
  Link as LinkIcon,
  Building2 as Building2Icon,
  Users as UsersIcon,
  FileUser as FileUserIcon,
  Clipboard as ClipboardIcon,
  User as UserIcon,
  Building as BuildingIcon,
  Clock as ClockIcon
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import { useAuthStore } from '../../stores/auth';
import AssetCard from '../UI/AssetCard.vue';
import TableRowActions from '../UI/TableRowActions.vue';

export default defineComponent({
  name: 'AgentCard',
  components: {
    AssetCard,
    LinkIcon,
    Building2Icon,
    UsersIcon,
    FileUserIcon,
    ClipboardIcon,
    TableRowActions,
    UserIcon,
    BuildingIcon,
    ClockIcon
  },
  props: {
    agent: {
      type: Object as PropType<AgentWithMemos | null>,
      default: null
    },
    viewMode: {
      type: String as PropType<'default' | 'table' | 'memostable'>,
      default: 'default'
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showEditButton: {
      type: Boolean,
      default: true
    },
    showDeleteButton: {
      type: Boolean,
      default: true
    },
    roles: {
      type: String,
      default: ''
    },
    memosCount: {
      type: Number,
      default: 0
    },
    // Nouvelles props pour les permissions et propriété
    isOwned: {
      type: Boolean,
      default: false
    },
    isPublic: {
      type: Boolean,
      default: false
    },
    showOwnershipBadge: {
      type: Boolean,
      default: true
    },
    userId: {
      type: String,
      default: undefined
    }
  },
  emits: ['add', 'edit', 'delete'],
  setup(props, { emit }) {
    const router = useRouter();
    const breadcrumbsStore = useBreadcrumbsStore();
    const authStore = useAuthStore();

    // Computed pour calculer automatiquement le nombre de mémos
    const computedMemosCount = computed((): number => {
      return props.agent?.memos?.length ?? props.memosCount;
    });

    function getDisplayNameParts(agent: AgentWithMemos | null): { firstName: string; lastName: string; affiliation: string; } {
      if (!agent) {
        return { firstName: '', lastName: '', affiliation: '' };
      }
      if (agent.is_legal_entity) {
        return {
          firstName: '',
          lastName: '',
          affiliation: agent.affiliation || ''
        };
      }
      return {
        firstName: agent.first_name || '',
        lastName: agent.last_name || '',
        affiliation: ''
      };
    }

    function getAureHalLink(idrefId: string): string {
      return `https://aurehal.archives-ouvertes.fr/author/${idrefId}`;
    }

    function getStructureLink(affiliationId: string): string {
      return `https://aurehal.archives-ouvertes.fr/structure/${affiliationId}`;
    }

    function getRoles(roles: string): string[] {
      return roles.split(',').map(role => role.trim()).filter(role => role !== '');
    }

    const handleClick = (): void => {
      if (!props.agent) return;
      
      try {
        // Sauvegarder l'état minimal dans l'historique (comme MaterialEntityCard)
        const minimalData = {
          uuid: props.agent.uuid,
          first_name: props.agent.first_name || '',
          last_name: props.agent.last_name || '',
          affiliation: props.agent.affiliation || ''
        };

        history.replaceState({ agent: minimalData }, '');
        
        // Mettre à jour le breadcrumb avec le nom de l'agent
        const agentName = props.agent.is_legal_entity 
          ? (props.agent.affiliation || 'Personne morale') 
          : `${props.agent.first_name || ''} ${props.agent.last_name || ''}`.trim() || 'Agent';
        
        breadcrumbsStore.updateBreadcrumbs([
          { name: 'Mes agents', path: '/myagents' },
          { name: agentName, path: `/agent/${props.agent.uuid}`, fullName: agentName }
        ]);
        
        // Naviguer vers la page de détail
        router.push(`/agent/${props.agent.uuid}`).catch((error) => {
          console.error('Erreur lors de la navigation:', error);
        });
      } catch (error) {
        console.error('Erreur lors de la navigation:', error);
      }
    };

    const handleDelete = (): void => {
      if (props.agent) {
        emit('delete', props.agent);
      }
    };

    const handleEdit = (): void => {
      if (props.agent) {
        emit('edit', props.agent);
      }
    };

    // Computed pour les permissions
    const canEditAgent = computed((): boolean => {
      return props.agent ? authStore.canEditAsset(props.agent) : false;
    });

    const canDeleteAgent = computed((): boolean => {
      return props.agent ? authStore.canDeleteAsset(props.agent) : false;
    });

    const formatDate = (date: string | Date | null): string => {
      if (!date) return '';
      const d = typeof date === 'string' ? new Date(date) : date;
      return d.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '/');
    };

    return {
      handleClick,
      handleEdit,
      handleDelete,
      getDisplayNameParts,
      getAureHalLink,
      getStructureLink,
      getRoles,
      canEditAgent,
      canDeleteAgent,
      formatDate,
      computedMemosCount
    };
  }
});
</script> 